import { AdminPageLayout } from '@/components/admin/AdminPageLayout';
import { EditProviderDialog } from '@/components/admin/EditProviderDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { trpc } from '@/utils/trpc';
import { useSuperAdmin } from '@/utils/use-super-admin';
import { useOrganization } from '@clerk/clerk-react';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import type { RouterOutput } from '@trapeze/server';
import {
  CalendarCheck,
  CalendarX,
  CheckCircle2,
  Edit,
  Loader2,
  Search,
  ShieldAlert,
  Stethoscope,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

export const Route = createFileRoute('/_protected/admin/providers')({
  component: ProvidersPage,
});

type Provider = RouterOutput['admin']['getProviders']['providers'][number];

function ProvidersPage() {
  const { isSuperAdmin, isLoading: isAdminLoading } = useSuperAdmin();
  const { organization, isLoaded: orgLoaded } = useOrganization();
  const router = useRouter();

  // State
  const [selectedEhrSystem, setSelectedEhrSystem] = useState<'imedicware'>('imedicware');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProviders, setSelectedProviders] = useState<Set<string>>(new Set());
  const [editDialog, setEditDialog] = useState<{ isOpen: boolean; provider: Provider | null }>({
    isOpen: false,
    provider: null,
  });

  // Use current organization ID from Clerk
  const currentOrganizationId = organization?.id;

  // Redirect non-super admins to dashboard
  useEffect(() => {
    if (!isAdminLoading && !isSuperAdmin) {
      router.navigate({ to: '/dashboard' });
    }
  }, [isAdminLoading, isSuperAdmin, router]);

  // tRPC queries and mutations
  const providersQuery = trpc.admin.getProviders.useQuery(
    { organizationId: currentOrganizationId || '', ehrSystem: selectedEhrSystem },
    { enabled: !!currentOrganizationId }
  );

  const updateProviderMutation = trpc.admin.updateProviderAgeRestrictions.useMutation({
    onSuccess: () => {
      toast.success('Provider age restrictions updated successfully');
      providersQuery.refetch();
      setEditDialog({ isOpen: false, provider: null });
    },
    onError: (error) => {
      toast.error(`Failed to update provider age restrictions: ${error.message}`);
    },
  });

  const handleSaveProvider = async (
    providerId: string,
    updates: { minAge?: number | null; maxAge?: number | null }
  ) => {
    if (!currentOrganizationId) return;

    await updateProviderMutation.mutateAsync({
      organizationId: currentOrganizationId,
      ehrSystem: selectedEhrSystem,
      providerId,
      ...updates,
    });
  };

  // Helper function to format age restrictions
  // Uses explicit null checks to handle age 0 correctly (newborns)
  const formatAgeRestrictions = (minAge: number | null, maxAge: number | null): string => {
    if (minAge !== null && maxAge !== null) {
      return `${minAge}-${maxAge} years`;
    }
    if (minAge !== null) {
      return `${minAge}+ years`;
    }
    if (maxAge !== null) {
      return `Up to ${maxAge} years`;
    }
    return 'No restrictions';
  };

  // Filter and search providers
  const filteredProviders = useMemo(() => {
    if (!providersQuery.data?.providers) return [];

    let filtered = providersQuery.data.providers;

    // Apply search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (provider) =>
          provider.name.toLowerCase().includes(search) ||
          provider.ehrId.toLowerCase().includes(search)
      );
    }

    return filtered;
  }, [providersQuery.data?.providers, searchTerm]);

  const handleSelectAll = () => {
    if (selectedProviders.size === filteredProviders.length) {
      setSelectedProviders(new Set());
    } else {
      setSelectedProviders(new Set(filteredProviders.map((p) => p.id)));
    }
  };

  // Loading state
  if (isAdminLoading || !orgLoaded) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  // Access denied for non-super admins
  if (!isSuperAdmin) {
    return (
      <div className="flex min-h-svh flex-col items-center justify-center gap-4 p-4">
        <div className="w-full max-w-md">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-destructive">
                <ShieldAlert className="h-5 w-5" />
                Access Denied
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                This page is only available to super administrators.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const stats = providersQuery.data?.stats || { total: 0, canSchedule: 0, cannotSchedule: 0 };

  return (
    <AdminPageLayout
      title="Provider Configuration"
      description="Configure provider scheduling settings and age restrictions"
      icon={Stethoscope}
      isSuperAdminPage={true}
    >
      <div className="flex flex-col gap-6">
        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">{stats.total}</div>
              <p className="text-xs text-muted-foreground">Total Providers</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-green-600">{stats.canSchedule}</div>
              <p className="text-xs text-muted-foreground">Can Schedule</p>
              <p className="text-xs text-muted-foreground mt-1">
                (Available for appointment booking)
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-gray-600">{stats.cannotSchedule}</div>
              <p className="text-xs text-muted-foreground">Cannot Schedule</p>
              <p className="text-xs text-muted-foreground mt-1">(Not available for booking)</p>
            </CardContent>
          </Card>
        </div>

        {/* Providers Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Providers</CardTitle>
                <CardDescription>
                  Configure provider scheduling and age restriction settings
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Search providers..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-8 w-64"
                  />
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Bulk Actions Bar (disabled) */}
            {selectedProviders.size > 0 && (
              <div className="mb-4 p-3 bg-muted rounded-lg flex items-center justify-between">
                <span className="text-sm font-medium">
                  {selectedProviders.size} provider(s) selected
                </span>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" disabled>
                    <Edit className="h-4 w-4 mr-1" />
                    Edit Selected
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setSelectedProviders(new Set())}>
                    Clear Selection
                  </Button>
                </div>
              </div>
            )}

            {/* Providers Table */}
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={
                          filteredProviders.length > 0 &&
                          selectedProviders.size === filteredProviders.length
                        }
                        onCheckedChange={handleSelectAll}
                        disabled
                      />
                    </TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>EHR ID</TableHead>
                    <TableHead>Can Schedule</TableHead>
                    <TableHead>Age Restrictions</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {providersQuery.isLoading ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8">
                        <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
                      </TableCell>
                    </TableRow>
                  ) : filteredProviders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        No providers found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredProviders.map((provider) => (
                      <TableRow key={provider.id}>
                        <TableCell>
                          <Checkbox
                            checked={selectedProviders.has(provider.id)}
                            onCheckedChange={(checked) => {
                              const newSet = new Set(selectedProviders);
                              if (checked) {
                                newSet.add(provider.id);
                              } else {
                                newSet.delete(provider.id);
                              }
                              setSelectedProviders(newSet);
                            }}
                            disabled
                          />
                        </TableCell>
                        <TableCell className="font-medium">{provider.name}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {provider.ehrId}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={provider.canSchedule ? 'default' : 'secondary'}
                            className="flex items-center gap-1 w-fit"
                          >
                            {provider.canSchedule ? (
                              <>
                                <CalendarCheck className="h-3 w-3" />
                                Can Schedule
                              </>
                            ) : (
                              <>
                                <CalendarX className="h-3 w-3" />
                                Cannot Schedule
                              </>
                            )}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">
                          {formatAgeRestrictions(provider.minAge, provider.maxAge)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8"
                            onClick={() => setEditDialog({ isOpen: true, provider })}
                          >
                            <Edit className="h-3 w-3 mr-1" />
                            Edit
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Info Box */}
            <div className="mt-6 p-4 bg-muted rounded-lg">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div className="space-y-1">
                  <p className="text-sm font-medium">About Provider Configuration</p>
                  <p className="text-sm text-muted-foreground">
                    Configure provider scheduling settings and age restrictions. Providers marked as
                    "Can Schedule" are available for appointment booking through the AI agent. Age
                    restrictions determine which patients can be scheduled with each provider based
                    on their age. Click "Edit" to configure age restrictions for individual
                    providers.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Edit Provider Dialog */}
      <EditProviderDialog
        provider={editDialog.provider}
        open={editDialog.isOpen}
        onOpenChange={(open) => setEditDialog({ ...editDialog, isOpen: open })}
        onSave={handleSaveProvider}
        isSaving={updateProviderMutation.isPending}
      />
    </AdminPageLayout>
  );
}
